import type { CandidateCardProps } from '@tempmee/react-blocks';
import { isPartialShift } from '~/commonComponents/CommonFunctions';
import { hasSpecialtyMismatch } from '~/hooks/useCandidate';
import { IJob, IMultipleCounters } from '~/models';
import { isBestInDentalWinner, toAttendance, toShiftCount } from '~/utils/candidateBadges';
import { getProfessionalName, toYearsOfExperience } from '~/utils/professional';

/** Whether the counter offer changes the shift's hourly rate. */
export const hasCounterRate = (
  counter: IMultipleCounters | null | undefined,
  job: Partial<IJob> | null | undefined,
): boolean => counter?.candidateRate != null && counter.candidateRate !== job?.rate;

/**
 * Whether the candidate countered the shift's terms at all — changed hours,
 * changed rate, or both. Drives the alert rule that suppresses "Confirm now to
 * secure coverage".
 */
export const hasCounterOffer = (
  counter: IMultipleCounters | null | undefined,
  job: Partial<IJob> | null | undefined,
): boolean => Boolean(isPartialShift(counter)) || hasCounterRate(counter, job);

/**
 * A candidate's signals, as both the list row and the topper read them — the
 * block's own contract, minus the click the topper has no use for.
 */
export type TCandidateCardData = Omit<CandidateCardProps, 'onCardClick'>;

/**
 * Counter offer → one `CandidateCard`.
 *
 * The five optional signals are the ones the block draws in a fixed order, so
 * this only decides whether each applies. `missingSpecialty` is the redesign's
 * replacement for the clickable specialty banner the legacy screen stacked at the
 * top of the page: on a list of candidates the mismatch belongs to a candidate,
 * not to the shift. It reads `hasSpecialtyMismatch`, not `!hasJobSpecialtyMatch`
 * — a shift that names no specialty flags nobody.
 */
export const toCandidateCardData = ({
  counter,
  job,
}: {
  counter: IMultipleCounters;
  job: Partial<IJob> | null | undefined;
}): TCandidateCardData => {
  const candidate = counter?.candidate;

  return {
    name: getProfessionalName(candidate) ?? '',
    avatarUrl: candidate?.profile_image,
    shiftCount: toShiftCount(candidate),
    years: toYearsOfExperience(candidate),
    rating: candidate?.average_rating ?? 0,
    attendance: toAttendance(candidate),
    isFavorite: counter?.isFavorite,
    isBestInDental: isBestInDentalWinner(candidate),
    missingSpecialty: hasSpecialtyMismatch(candidate, job) ? 'Missing Specialty' : undefined,
    companyExperience:
      counter?.hasWorkedWithOffice || counter?.hasWorkedWithDSO ? 'Company Experience' : undefined,
    partialShift: isPartialShift(counter) ? 'Partial Shift' : undefined,
    counterRate: hasCounterRate(counter, job) ? 'Counter Rate' : undefined,
  };
};
